import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import { AccountSelect, DealSelect, usd, parseCents, centsToInput, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

// Chart of accounts, categorization rules, the QBO cutover date, and the audit log.

const TYPES = ['asset', 'liability', 'equity', 'income', 'cogs', 'expense'];
const TYPE_LABEL = { asset: 'Asset', liability: 'Liability', equity: 'Equity', income: 'Income', cogs: 'Cost of goods sold', expense: 'Expense', suspense: 'To review' };

export default function SetupTab({ me, accounts, deals, reloadShared }) {
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState(null);
  const [editAcct, setEditAcct] = useState(null);
  const [editRule, setEditRule] = useState(null);
  const [cutover, setCutover] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get('/books/rules'), api.get('/books/settings')]);
      setRules(r.rules); setSettings(s.settings); setCutover(s.settings.cutover_date || ''); setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const byId = new Map(accounts.map((a) => [a.id, a]));

  const saveCutover = async () => {
    try { const s = await api.patch('/books/settings', { cutover_date: cutover || null }); setSettings(s.settings); setMsg('Cutover date saved.'); } catch (e) { setError(e.message); }
  };
  const delRule = async (r) => {
    if (!window.confirm(`Delete the rule "${r.match_text}"?`)) return;
    try { await api.delete(`/books/rules/${r.id}`); load(); } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-5">
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {msg && <p className="text-sm text-emerald-700 dark:text-emerald-400">{msg}</p>}

      <section className={cardCls}>
        <h2 className="mb-1 font-semibold text-[var(--text-primary)]">QuickBooks history cutover</h2>
        <p className="mb-2 text-xs text-[var(--text-secondary)]">History up to this date comes from the QBO General Ledger export (loaded with scripts/import-qbo-gl.js). Relay imports skip bank lines on or before it so nothing is counted twice. QBO is only read, never written.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" className={inputCls} value={cutover} onChange={(e) => setCutover(e.target.value)} disabled={!me.can_edit} aria-label="Cutover date" />
          {me.can_edit && <button className={btn2Cls} onClick={saveCutover} disabled={(settings?.cutover_date || '') === cutover}>Save</button>}
          {!settings?.cutover_date && <span className="text-xs text-[var(--text-secondary)]">Not set: every Relay line is imported.</span>}
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">Chart of accounts</h2>
          {me.can_edit && <button className={btn2Cls} onClick={() => setEditAcct({})}><Plus size={14} /> Account</button>}
        </div>
        <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800"><tr><th className={thCls}>Code</th><th className={thCls}>Name</th><th className={thCls}>Type</th><th className={thCls}>Flags</th><th /></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className={`border-t border-[var(--border)] ${a.active ? '' : 'opacity-50'}`}>
                  <td className={`${tdCls} tabular-nums`}>{a.code}</td><td className={tdCls}>{a.name}</td><td className={tdCls}>{TYPE_LABEL[a.type]}</td>
                  <td className={`${tdCls} text-xs text-[var(--text-secondary)]`}>{[a.is_cash && 'bank/cash', a.deal_cost && 'deal cost', a.member && `member: ${a.member}`, !a.active && 'inactive'].filter(Boolean).join(', ')}</td>
                  <td className={tdCls}>{me.can_edit && a.type !== 'suspense' && <button className="text-[var(--text-secondary)]" onClick={() => setEditAcct(a)} aria-label="Edit"><Pencil size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">Categorization rules</h2>
          {me.can_edit && <button className={btn2Cls} onClick={() => setEditRule({})}><Plus size={14} /> Rule</button>}
        </div>
        {rules.length === 0 ? <p className="text-sm text-[var(--text-secondary)]">No rules yet. Tick "Remember" when categorizing a bank line to add one.</p> : (
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr><th className={thCls}>When the bank line contains</th><th className={thCls}>Direction</th><th className={thCls}>Category</th><th className={thCls}>Priority</th><th /></tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className={`border-t border-[var(--border)] ${r.active === false ? 'opacity-50' : ''}`}>
                    <td className={tdCls}>"{r.match_text}" <span className="text-xs text-[var(--text-secondary)]">in {r.match_field}{r.min_cents != null ? `, at least ${usd(r.min_cents)}` : ''}{r.max_cents != null ? `, at most ${usd(r.max_cents)}` : ''}</span></td>
                    <td className={tdCls}>{r.direction === 'in' ? 'Money in' : r.direction === 'out' ? 'Money out' : 'Either'}</td>
                    <td className={tdCls}>{byId.get(r.account_id)?.name}{r.deal_id ? ` / ${deals.find((d) => d.id === r.deal_id)?.name || 'deal'}` : ''}</td>
                    <td className={tdCls}>{r.priority}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{me.can_edit && <>
                      <button className="mr-2 text-[var(--text-secondary)]" onClick={() => setEditRule(r)} aria-label="Edit"><Pencil size={14} /></button>
                      <button className="text-[var(--text-secondary)]" onClick={() => delRule(r)} aria-label="Delete"><Trash2 size={14} /></button>
                    </>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">Audit log</h2>
          {!audit && <button className={btn2Cls} onClick={() => api.get('/books/audit?limit=200').then((r) => setAudit(r.audit)).catch((e) => setError(e.message))}>Show recent changes</button>}
        </div>
        {audit && (
          <ul className="max-h-80 overflow-y-auto rounded-xl border border-[var(--border)] bg-white p-2 text-xs dark:bg-slate-900">
            {audit.map((a) => <li key={a.id} className="border-b border-[var(--border)] py-1 last:border-0">{new Date(a.created_at).toLocaleString()} / {a.actor || 'system'} / {a.action} / {a.table_name.replace('books_', '')} {a.row_id?.slice(0, 8)}</li>)}
          </ul>
        )}
      </section>

      {editAcct && <AccountForm account={editAcct.id ? editAcct : null} onClose={() => setEditAcct(null)} onSaved={() => { setEditAcct(null); reloadShared(); }} />}
      {editRule && <RuleForm rule={editRule.id ? editRule : null} accounts={accounts} deals={deals} onClose={() => setEditRule(null)} onSaved={() => { setEditRule(null); load(); }} />}
    </div>
  );
}

function AccountForm({ account, onClose, onSaved }) {
  const [f, setF] = useState({ code: account?.code || '', name: account?.name || '', type: account?.type || 'expense', subtype: account?.subtype || '', is_cash: !!account?.is_cash, deal_cost: !!account?.deal_cost, member: account?.member || '', active: account ? account.active : true, description: account?.description || '' });
  const [error, setError] = useState('');
  const save = async () => {
    const body = { code: f.code, name: f.name, type: f.type, subtype: f.subtype || 'other', is_cash: f.is_cash, deal_cost: f.deal_cost, member: f.member || null, active: f.active, description: f.description || null };
    try {
      if (account) {
        const changed = Object.fromEntries(Object.entries(body).filter(([k, v]) => (account[k] ?? null) !== (v ?? null)));
        await api.patch(`/books/accounts/${account.id}`, { ...changed, revision: account.updated_at });
      } else await api.post('/books/accounts', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal onClose={onClose} title={account ? `Edit ${account.code}` : 'New account'} maxWidth="max-w-md">
      <div className="space-y-2 text-sm">
        <label className="block">Code (4 to 6 digits) <input className={`${inputCls} w-full`} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></label>
        <label className="block">Name <input className={`${inputCls} w-full`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="block">Type
          <select className={`${inputCls} w-full`} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}</select>
        </label>
        <label className="block">Subtype (optional label, e.g. bank, ap, capital) <input className={`${inputCls} w-full`} value={f.subtype} onChange={(e) => setF({ ...f, subtype: e.target.value })} /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_cash} onChange={(e) => setF({ ...f, is_cash: e.target.checked })} /> Bank or cash account (asset only; used for tie-outs)</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.deal_cost} onChange={(e) => setF({ ...f, deal_cost: e.target.checked })} /> Direct deal cost (deducted from a deal's net proceeds when tagged)</label>
        <label className="block">Member (capital, distribution, reimbursement accounts)
          <select className={`${inputCls} w-full`} value={f.member} onChange={(e) => setF({ ...f, member: e.target.value })}><option value="">None</option>{['Chad', 'Steve', 'Chase'].map((m) => <option key={m}>{m}</option>)}</select>
        </label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
        {error && <p className="text-rose-600">{error}</p>}
        <button className={btnCls} onClick={save} disabled={!f.code || !f.name}>Save</button>
      </div>
    </Modal>
  );
}

function RuleForm({ rule, accounts, deals, onClose, onSaved }) {
  const [f, setF] = useState({ match_text: rule?.match_text || '', match_field: rule?.match_field || 'any', direction: rule?.direction || 'any', min: centsToInput(rule?.min_cents), max: centsToInput(rule?.max_cents), account_id: rule?.account_id || null, deal_id: rule?.deal_id || null, priority: rule?.priority ?? 100, active: rule ? rule.active !== false : true });
  const [error, setError] = useState('');
  const save = async () => {
    const body = { match_text: f.match_text, match_field: f.match_field, direction: f.direction, min_cents: f.min ? parseCents(f.min) : null, max_cents: f.max ? parseCents(f.max) : null, account_id: f.account_id, deal_id: f.deal_id || null, priority: Number(f.priority), active: f.active };
    try {
      if (rule) await api.patch(`/books/rules/${rule.id}`, body); else await api.post('/books/rules', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal onClose={onClose} title={rule ? 'Edit rule' : 'New rule'} maxWidth="max-w-md">
      <div className="space-y-2 text-sm">
        <label className="block">Bank line contains <input className={`${inputCls} w-full`} value={f.match_text} onChange={(e) => setF({ ...f, match_text: e.target.value })} placeholder="e.g. pinnacle" /></label>
        <div className="flex gap-2">
          <select className={inputCls} value={f.match_field} onChange={(e) => setF({ ...f, match_field: e.target.value })} aria-label="Where"><option value="any">in payee or description</option><option value="payee">in payee</option><option value="description">in description</option></select>
          <select className={inputCls} value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} aria-label="Direction"><option value="any">money in or out</option><option value="in">money in</option><option value="out">money out</option></select>
        </div>
        <div className="flex gap-2">
          <input className={`${inputCls} w-full`} placeholder="Min amount (optional)" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })} />
          <input className={`${inputCls} w-full`} placeholder="Max amount (optional)" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })} />
        </div>
        <AccountSelect accounts={accounts} value={f.account_id} onChange={(v) => setF({ ...f, account_id: v })} exclude={['9999']} className="w-full" />
        <DealSelect deals={deals} value={f.deal_id} onChange={(v) => setF({ ...f, deal_id: v })} className="w-full" />
        <label className="block">Priority (lower runs first) <input className={`${inputCls} w-24`} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} /></label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
        {error && <p className="text-rose-600">{error}</p>}
        <button className={btnCls} onClick={save} disabled={!f.match_text.trim() || !f.account_id}>Save rule</button>
      </div>
    </Modal>
  );
}
